import React, { useContext, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Store } from '../../Store';
import { toast } from 'react-toastify';
import { getError } from '../../utils';
import { Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import authService from '../../services/auth.service';

export default function SignUpScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { search } = useLocation();
  const [searchParams] = useSearchParams();

  const returnTo = searchParams.get('returnTo');
  const redirectInUrl = new URLSearchParams(search).get('redirect');
  const redirect = returnTo || redirectInUrl || '/';

  const [nombre, setNombre] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const { state, dispatch: ctxDispatch } = useContext(Store);
  const { userInfo } = state;

  useEffect(() => {
    if (userInfo) navigate(redirect);
  }, [navigate, redirect, userInfo]);

  const submitHandler = async e => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast.error(t('auth.passwordsDontMatch'));
      return;
    }
    setSubmitting(true);
    try {
      const data = await authService.register({
        nombre,
        email,
        password,
      });
      try {
        const loginData = await authService.login({ email, password });
        ctxDispatch({ type: 'USER_SIGNIN', payload: loginData });
        localStorage.setItem('userInfo', JSON.stringify(loginData));
      } catch {
        /* user must verify email before login */
      }
      toast.success(data.message || t('auth.register'));
      navigate(redirect);
    } catch (err) {
      toast.error(getError(err));
    }
    setSubmitting(false);
  };

  const inputStyle = {
    width: '100%',
    backgroundColor: '#F5F5F5',
    border: '1px solid #E8E8E8',
    borderRadius: '8px',
    padding: '12px 14px',
    fontSize: '14px',
    color: '#1A1A1A',
    outline: 'none',
  };
  const labelStyle = {
    display: 'block',
    fontSize: '12px',
    fontWeight: '500',
    color: '#7C7C7C',
    marginBottom: '6px',
  };

  return (
    <div
      className='flex min-h-screen items-center justify-center px-4 py-10'
      style={{ backgroundColor: 'var(--background-color, #F4F4F4)' }}
    >
      <Helmet>
        <title>{t('auth.createAccount')}</title>
      </Helmet>

      <div className='w-full max-w-sm rounded-2xl shadow-lg overflow-hidden bg-white'>
        {/* Header */}
        <div
          className='flex items-center px-5 py-4'
          style={{
            backgroundColor: 'var(--primaryColor)',
            color: 'var(--navFontColor)',
          }}
        >
          <button
            type='button'
            onClick={() => navigate(-1)}
            className='mr-3 flex h-8 w-8 shrink-0 items-center justify-center rounded-full hover:bg-white/20 transition-colors'
            style={{ color: 'var(--navFontColor)' }}
          >
            <ArrowLeft size={18} />
          </button>
          <h1 className='text-base font-semibold' style={{ color: 'var(--navFontColor)' }}>
            {t('auth.createAccount')}
          </h1>
        </div>

        {/* Form */}
        <form onSubmit={submitHandler} className='px-6 py-6 space-y-4'>
          {/* Nombre */}
          <div>
            <label style={labelStyle}>{t('auth.name')}</label>
            <input
              type='text'
              required
              value={nombre}
              onChange={e => setNombre(e.target.value)}
              placeholder='Juan García'
              style={inputStyle}
              autoComplete='name'
            />
          </div>

          {/* Email */}
          <div>
            <label style={labelStyle}>{t('auth.email')}</label>
            <input
              type='email'
              required
              value={email}
              onChange={e => setEmail(e.target.value.toLowerCase())}
              placeholder='mail@ejemplo.com'
              style={inputStyle}
              autoComplete='email'
            />
          </div>

          {/* Contraseña */}
          <div>
            <label style={labelStyle}>{t('auth.password')}</label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPass ? 'text' : 'password'}
                required
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder='••••••••'
                style={{ ...inputStyle, paddingRight: '44px' }}
                autoComplete='new-password'
              />
              <button
                type='button'
                onClick={() => setShowPass(v => !v)}
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#ABABAB',
                  padding: 0,
                }}
              >
                {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          {/* Confirmar contraseña */}
          <div>
            <label style={labelStyle}>{t('auth.repeatPassword')}</label>
            <div style={{ position: 'relative' }}>
              <input
                type={showConfirm ? 'text' : 'password'}
                required
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                placeholder='••••••••'
                style={{ ...inputStyle, paddingRight: '44px' }}
                autoComplete='new-password'
              />
              <button
                type='button'
                onClick={() => setShowConfirm(v => !v)}
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#ABABAB',
                  padding: 0,
                }}
              >
                {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {confirmPassword && password !== confirmPassword && (
              <p style={{ fontSize: '11px', color: '#d32f2f', marginTop: '4px' }}>
                {t('auth.passwordsDontMatch')}
              </p>
            )}
          </div>

          {/* Submit */}
          <button
            type='submit'
            disabled={submitting}
            className='w-full rounded-xl py-3 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50'
            style={{
              backgroundColor: 'var(--secondaryColor)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.18)',
              border: 'none',
              cursor: submitting ? 'not-allowed' : 'pointer',
              marginTop: '8px',
            }}
          >
            {submitting ? t('common.loading') : t('auth.createAccount')}
          </button>

          {/* Footer link */}
          <p className='text-center' style={{ fontSize: '13px', color: '#7C7C7C' }}>
            {t('auth.alreadyHaveAccount')}{' '}
            <Link
              to={`/signin${redirect !== '/' ? `?returnTo=${encodeURIComponent(redirect)}` : ''}`}
              style={{
                color: 'var(--secondaryColor)',
                fontWeight: '600',
                textDecoration: 'none',
              }}
            >
              {t('auth.login')}
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
